
import { useState } from 'react';

export default function AICapabilities() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section id="ai-capabilities" className="py-20 bg-white relative">
      {/* White Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: 'url("https://static.readdy.ai/image/543e6025ab8aef1bb1f6ad8fb7713dea/d285a00fd4ef2cfa28a18bd50eb5f641.png")'
        }}
      >
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header with Toggle Button */}
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Die Stärken von <span className="text-teal-600">KI</span>
          </h2>
          
          {/* Toggle Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Inhalt zuklappen" : "Inhalt aufklappen"}
          >
            <span>{isExpanded ? "Weniger anzeigen" : "Mehr erfahren"}</span>
            <i 
              className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
            ></i>
          </button>
        </div>

        {/* Expandable Content */}
        <div 
          className={`overflow-hidden transition-all duration-500 ease-in-out ${
            isExpanded ? 'max-h-[10000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="pt-8">
            <p className="text-xl text-gray-700 max-w-3xl mx-auto text-center mb-16">
              Mithilfe fortschrittlicher KI erzielen wir nachhaltige Marketingerfolge und messbare Ergebnisse.
            </p>

            <div className="space-y-8">
              {/* Machine Learning Optimization */}
              <div className="flex flex-col lg:flex-row items-center gap-12">
                <div className="flex-1">
                  <img 
                    src="https://static.readdy.ai/image/543e6025ab8aef1bb1f6ad8fb7713dea/a52eda65c90c743a4974d77f1813f12f.png"
                    alt="Machine Learning Optimization"
                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                  />
                </div>
                <div className="flex-1 space-y-6">
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Lernfähigkeit von KI</h3>
                  <p className="text-xl text-gray-700 leading-relaxed">
                    KI-Algorithmen lernen kontinuierlich aus Daten, wodurch sich ihr Verständnis vertieft und sich ihre Antworten automatisch optimieren.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gray-900/80 backdrop-blur-md p-4 rounded-lg border border-gray-700 flex items-center justify-center text-center">
                      <div className="text-teal-400 font-bold text-base">Steigerung der Genauigkeit</div>
                    </div>
                    <div className="bg-gray-900/80 backdrop-blur-md p-4 rounded-lg border border-gray-700 flex items-center justify-center text-center">
                      <div className="text-teal-400 font-bold text-base">Zukünftige Kostenreduktion</div>
                    </div>
                    <div className="bg-gray-900/80 backdrop-blur-md p-4 rounded-lg border border-gray-700 flex items-center justify-center text-center">
                      <div className="text-teal-400 font-bold text-base">Präzise Steuerung der Informationsquellen</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Predictive Analytics - WITH ENHANCED ANIMATION */}
              <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
                <div className="flex-1 relative">
                  {/* Animated Container */}
                  <div className="relative w-full h-80 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-2xl overflow-hidden">
                    {/* Background Grid */}
                    <div className="absolute inset-0 opacity-20">
                      <div className="absolute inset-0" style={{
                        backgroundImage: 'linear-gradient(rgba(20, 184, 166, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 184, 166, 0.3) 1px, transparent 1px)',
                        backgroundSize: '40px 40px'
                      }}></div>
                    </div>

                    {/* Floating Data Particles */}
                    <div className="absolute inset-0">
                      {[...Array(20)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-1.5 h-1.5 bg-teal-400 rounded-full animate-float"
                          style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                            animationDuration: `${3 + Math.random() * 2}s`
                          }}
                        ></div>
                      ))}
                    </div>

                    {/* Animated Circle Diagram (Donut Chart) - Top Left */}
                    <div className="absolute top-8 left-8 w-32 h-32">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        {/* Background circle */}
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="rgba(20, 184, 166, 0.1)"
                          strokeWidth="8"
                        />
                        {/* Animated segments */}
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#14B8A6"
                          strokeWidth="8"
                          strokeDasharray="188.4"
                          strokeDashoffset="0"
                          className="animate-draw-circle"
                          style={{ animationDelay: '0s' }}
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#06B6D4"
                          strokeWidth="8"
                          strokeDasharray="125.6 62.8"
                          strokeDashoffset="-47.1"
                          className="animate-draw-circle"
                          style={{ animationDelay: '0.3s' }}
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#0891B2"
                          strokeWidth="8"
                          strokeDasharray="62.8 125.6"
                          strokeDashoffset="-109.9"
                          className="animate-draw-circle"
                          style={{ animationDelay: '0.6s' }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-teal-400 text-xs font-semibold">Analyse</div>
                        </div>
                      </div>
                    </div>

                    {/* Animated Bar Chart - Bottom Left */}
                    <div className="absolute bottom-8 left-8 w-40 h-24 flex items-end justify-between gap-2">
                      {[70, 45, 85, 60, 90, 55].map((height, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center">
                          <div 
                            className="w-full bg-gradient-to-t from-teal-500 to-cyan-400 rounded-t animate-grow shadow-lg shadow-teal-500/30"
                            style={{
                              height: `${height}%`,
                              animationDelay: `${i * 0.15}s`
                            }}
                          ></div>
                        </div>
                      ))}
                    </div>

                    {/* Animated Line Graph - Top Right */}
                    <svg className="absolute top-12 right-8 w-48 h-28" viewBox="0 0 200 100" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.8" />
                          <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.8" />
                        </linearGradient>
                        <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      {/* Area fill */}
                      <path
                        d="M 0 70 Q 25 50, 50 45 T 100 35 T 150 25 T 200 20 L 200 100 L 0 100 Z"
                        fill="url(#areaGradient)"
                        className="animate-draw"
                      />
                      {/* Line */}
                      <path
                        d="M 0 70 Q 25 50, 50 45 T 100 35 T 150 25 T 200 20"
                        fill="none"
                        stroke="url(#lineGradient)"
                        strokeWidth="2"
                        className="animate-draw"
                      />
                      {/* Animated dots */}
                      {[0, 50, 100, 150, 200].map((x, i) => (
                        <circle
                          key={i}
                          cx={x}
                          cy={70 - i * 12.5}
                          r="3"
                          fill="#14B8A6"
                          className="animate-pulse"
                          style={{ animationDelay: `${i * 0.2}s` }}
                        />
                      ))}
                    </svg>

                    {/* Animated Progress Bars - Bottom Right */}
                    <div className="absolute bottom-8 right-8 w-40 space-y-2">
                      {[
                        { width: 85, label: 'Effizienz', color: 'from-teal-500 to-teal-400' },
                        { width: 70, label: 'Qualität', color: 'from-cyan-500 to-cyan-400' },
                        { width: 95, label: 'Performance', color: 'from-teal-600 to-cyan-500' }
                      ].map((bar, i) => (
                        <div key={i} className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-teal-400 text-xs font-semibold">{bar.label}</span>
                          </div>
                          <div className="w-full h-2 bg-gray-700/50 rounded-full overflow-hidden">
                            <div 
                              className={`h-full bg-gradient-to-r ${bar.color} rounded-full animate-progress shadow-lg shadow-teal-500/30`}
                              style={{
                                width: `${bar.width}%`,
                                animationDelay: `${i * 0.2}s`
                              }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Scanning Line Effect */}
                    <div className="absolute inset-0 overflow-hidden">
                      <div className="absolute w-full h-1 bg-gradient-to-r from-transparent via-teal-400 to-transparent animate-scan"></div>
                    </div>

                    {/* Pulsing Rings */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <div className="w-20 h-20 border-2 border-teal-400 rounded-full animate-ping opacity-20"></div>
                      <div className="absolute inset-0 w-20 h-20 border-2 border-cyan-400 rounded-full animate-ping opacity-20" style={{ animationDelay: '0.5s' }}></div>
                      <div className="absolute inset-0 w-20 h-20 border-2 border-teal-500 rounded-full animate-ping opacity-20" style={{ animationDelay: '1s' }}></div>
                    </div>

                    {/* Rotating Radar Effect */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32">
                      <div className="absolute inset-0 animate-spin-slow">
                        <div className="absolute top-0 left-1/2 w-0.5 h-16 bg-gradient-to-b from-teal-400 to-transparent transform -translate-x-1/2"></div>
                      </div>
                    </div>

                    {/* Data Flow Lines */}
                    <svg className="absolute inset-0 w-full h-full opacity-30">
                      <defs>
                        <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#14B8A6" stopOpacity="0" />
                          <stop offset="50%" stopColor="#14B8A6" stopOpacity="0.6" />
                          <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      {[...Array(5)].map((_, i) => (
                        <line
                          key={i}
                          x1="0"
                          y1={`${20 + i * 15}%`}
                          x2="100%"
                          y2={`${20 + i * 15}%`}
                          stroke="url(#flowGradient)"
                          strokeWidth="1"
                          className="animate-flow"
                          style={{ animationDelay: `${i * 0.3}s` }}
                        />
                      ))}
                    </svg>
                  </div>
                </div>
                <div className="flex-1 space-y-6">
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Data Analytics</h3>
                  <p className="text-xl text-gray-700 leading-relaxed">
                    Die Analyse von erhobenen Daten hilft dabei besser auf die Kundenbedürfnisse einzugehen.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gray-900/80 backdrop-blur-md p-4 rounded-lg border border-gray-700 flex items-center justify-center text-center">
                      <div className="text-teal-400 font-bold text-base">Schaffung & Ausbau einer Datenbasis</div>
                    </div>
                    <div className="bg-gray-900/80 backdrop-blur-md p-4 rounded-lg border border-gray-700 flex items-center justify-center text-center">
                      <div className="text-teal-400 font-bold text-base">schnellere Erkenntnisse</div>
                    </div>
                    <div className="bg-gray-900/80 backdrop-blur-md p-4 rounded-lg border border-gray-700 flex items-center justify-center text-center">
                      <div className="text-teal-400 font-bold text-base">Bessere Entscheidungsfindung</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Natural Language Processing */}
              <div className="flex flex-col lg:flex-row items-center gap-12">
                <div className="flex-1">
                  <img 
                    src="https://static.readdy.ai/image/543e6025ab8aef1bb1f6ad8fb7713dea/fdf850ab0f497eeaf83dd73bc6d72502.png"
                    alt="Natural Language Processing"
                    className="w-full h-64 object-cover rounded-xl shadow-2xl"
                  />
                </div>
                <div className="flex-1 space-y-6">
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Prozessoptimierung</h3>
                  <p className="text-xl text-gray-700 leading-relaxed">
                    Durch die Zusammenarbeit von Mensch und KI können Prozesse effizienter gestaltet werden.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gray-900/80 backdrop-blur-md p-4 rounded-lg border border-gray-700 flex items-center justify-center text-center">
                      <div className="text-teal-400 font-bold text-base">Effizienzsteigerung</div>
                    </div>
                    <div className="bg-gray-900/80 backdrop-blur-md p-4 rounded-lg border border-gray-700 flex items-center justify-center text-center">
                      <div className="text-teal-400 font-bold text-base">Personalisierte Schulungen</div>
                    </div>
                    <div className="bg-gray-900/80 backdrop-blur-md p-4 rounded-lg border border-gray-700 flex items-center justify-center text-center">
                      <div className="text-teal-400 font-bold text-base">Skalierbare Lösungen</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="mt-20 text-center">
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                Jetzt kostenlos Beraten lassen
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
